import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { createPortal } from 'react-dom';
import { cx } from './ui.tsx';

/**
 * Transient confirmations.
 *
 * A dialog demands a decision; a toast reports something that already
 * happened. Using a dialog for "Saved" would make the user dismiss a box for
 * every successful action, which teaches them to click OK without reading —
 * and then they click OK on the one that mattered.
 *
 * Everything clears itself. Errors linger longer than confirmations — a
 * failure that vanishes in four seconds is one the user will not have read —
 * but they no longer sit there until dismissed. Holding them turned a couple
 * of retries into a stack of identical red boxes covering the corner of the
 * screen, which is its own kind of unreadable, and the close button is still
 * there for anyone who wants them gone sooner.
 */

export type ToastTone = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  tone: ToastTone;
  message: string;
  detail?: string | undefined;
  /**
   * Work still going on: 0–1 when the size is known, null when it is not.
   *
   * A toast with this set does not clear itself, because the thing it is
   * about has not finished.
   */
  progress?: number | null | undefined;
}

/**
 * A job in progress, and the three ways it ends.
 *
 * Handed back by `progress` so the caller can report as it goes without
 * holding a toast id. Calling `done` or `fail` turns the same card into an
 * ordinary confirmation, which then clears itself — so a download that takes
 * five minutes occupies one card the whole time rather than appearing at the
 * end, when the user has already decided the button is broken.
 */
export interface ToastProgress {
  /** `fraction` is 0–1, or null when the total is unknown. */
  update(fraction: number | null, detail?: string): void;
  done(message?: string, detail?: string): void;
  fail(message: string, detail?: string): void;
}

interface ToastApi {
  success(message: string, detail?: string): void;
  error(message: string, detail?: string): void;
  info(message: string, detail?: string): void;
  /** A card that stays until the work it describes is over. */
  progress(message: string, detail?: string): ToastProgress;
}

const ToastContext = createContext<ToastApi | null>(null);

export function useToast(): ToastApi {
  const api = useContext(ToastContext);
  if (!api) throw new Error('useToast must be used inside <ToastProvider>');
  return api;
}

const DISMISS_AFTER = 4000;
/**
 * Longer, because a failure now carries the reason under it.
 *
 * The export failures used to print inline in the title band, which hides
 * itself as you scroll — so the message went away on its own and there was no
 * way to bring it back or to close it. They are toasts now, and a toast
 * carrying a server reference the user may want to quote needs long enough to
 * read twice. The close button is there for anyone who wants it gone sooner.
 */
const DISMISS_ERROR_AFTER = 8000;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const nextId = useRef(1);

  const remove = useCallback((id: number) => {
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (tone: ToastTone, message: string, detail?: string) => {
      const id = nextId.current++;
      setToasts((current) => [...current.slice(-3), { id, tone, message, detail }]);
      setTimeout(() => remove(id), tone === 'error' ? DISMISS_ERROR_AFTER : DISMISS_AFTER);
    },
    [remove],
  );

  /**
   * A card that stays put while something is happening.
   *
   * Deliberately not on the `slice(-3)` trim that the transient ones use — a
   * running job pushed off the end by three confirmations would leave the user
   * watching nothing again. There is one of these at a time in practice, and
   * it is removed when the work it describes ends.
   */
  const progress = useCallback(
    (message: string, detail?: string): ToastProgress => {
      const id = nextId.current++;
      setToasts((current) => [...current, { id, tone: 'info', message, detail, progress: null }]);

      const patch = (next: Partial<Toast>) => {
        setToasts((current) =>
          current.map((toast) => (toast.id === id ? { ...toast, ...next } : toast)),
        );
      };

      const settle = (tone: ToastTone, message: string, detail?: string) => {
        patch({ tone, message, detail, progress: undefined });
        setTimeout(() => remove(id), tone === 'error' ? DISMISS_ERROR_AFTER : DISMISS_AFTER);
      };

      return {
        update: (fraction, nextDetail) =>
          patch({
            progress: fraction === null ? null : Math.max(0, Math.min(1, fraction)),
            ...(nextDetail === undefined ? {} : { detail: nextDetail }),
          }),
        done: (nextMessage, nextDetail) => settle('success', nextMessage ?? message, nextDetail),
        fail: (nextMessage, nextDetail) => settle('error', nextMessage, nextDetail),
      };
    },
    [remove],
  );

  const api = useMemo<ToastApi>(
    () => ({
      success: (message, detail) => push('success', message, detail),
      error: (message, detail) => push('error', message, detail),
      info: (message, detail) => push('info', message, detail),
      progress,
    }),
    [push, progress],
  );

  return (
    <ToastContext.Provider value={api}>
      {children}
      {createPortal(
        <div
          className="pointer-events-none fixed bottom-4 right-4 z-[95] flex w-80 flex-col gap-2"
          // Errors interrupt; confirmations wait their turn.
          aria-live="polite"
          aria-atomic="false"
        >
          {toasts.map((toast) => (
            <ToastCard key={toast.id} toast={toast} onDismiss={() => remove(toast.id)} />
          ))}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  );
}

const TONES: Record<ToastTone, { box: string; icon: string; glyph: ReactNode }> = {
  success: {
    box: 'border-[var(--success)] bg-success-bg',
    icon: 'text-[var(--success)]',
    glyph: <CheckGlyph />,
  },
  error: {
    box: 'border-[var(--danger)] bg-danger-bg',
    icon: 'text-[var(--danger)]',
    glyph: <CrossGlyph />,
  },
  info: {
    box: 'border-[var(--blue-600)] bg-[var(--blue-50)]',
    icon: 'text-[var(--blue-700)]',
    glyph: <InfoGlyph />,
  },
};

function ToastCard({ toast, onDismiss }: { toast: Toast; onDismiss(): void }) {
  const tone = TONES[toast.tone];

  return (
    <div
      role={toast.tone === 'error' ? 'alert' : 'status'}
      className={cx(
        'pointer-events-auto flex items-start gap-2.5 rounded-lg border-l-4 bg-surface px-3 py-2.5 shadow-lg',
        tone.box,
      )}
    >
      <span className={cx('mt-0.5 shrink-0', tone.icon)} aria-hidden>
        {tone.glyph}
      </span>

      <div className="min-w-0 flex-1">
        <p className="text-body-sm font-medium text-ink">{toast.message}</p>
        {toast.detail ? <p className="mt-0.5 text-micro text-body">{toast.detail}</p> : null}
        {toast.progress !== undefined ? <Bar fraction={toast.progress} /> : null}
      </div>

      <button
        type="button"
        onClick={onDismiss}
        aria-label="Dismiss"
        className="-mr-1 -mt-1 shrink-0 rounded p-1 text-muted hover:bg-sunken hover:text-ink"
      >
        <svg width="12" height="12" viewBox="0 0 12 12" stroke="currentColor" strokeWidth="1.6">
          <path d="M2 2l8 8M10 2l-8 8" strokeLinecap="round" />
        </svg>
      </button>
    </div>
  );
}

/**
 * How far along, or that it is going at all.
 *
 * A server that sends a Content-Length gives a real percentage; one that
 * streams without a total gives a bar that slides, which says the same thing
 * a spinner says and takes no more room than the determinate one. Both beat
 * the previous answer, which was no bar and no toast — a Download button that
 * did nothing observable for five minutes and read as broken.
 */
function Bar({ fraction }: { fraction: number | null }) {
  return (
    <div
      className="mt-1.5 h-1 overflow-hidden rounded-full bg-sunken"
      role="progressbar"
      {...(fraction === null
        ? {}
        : {
            'aria-valuenow': Math.round(fraction * 100),
            'aria-valuemin': 0,
            'aria-valuemax': 100,
          })}
    >
      <div
        className={cx(
          'h-full rounded-full bg-[var(--blue-600)]',
          fraction === null ? 'w-1/3 animate-[toast-slide_1.1s_ease-in-out_infinite]' : '',
        )}
        style={fraction === null ? undefined : { width: `${Math.round(fraction * 100)}%` }}
      />
    </div>
  );
}

function CheckGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="8" cy="8" r="6.5" strokeWidth="1.3" />
      <path d="M5 8.2l2 2 4-4.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function CrossGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    >
      <circle cx="8" cy="8" r="6.5" />
      <path d="M5.5 5.5l5 5M10.5 5.5l-5 5" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

function InfoGlyph() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.3"
    >
      <circle cx="8" cy="8" r="6.5" />
      <path d="M8 7.2v4" strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="8" cy="4.9" r="0.7" fill="currentColor" stroke="none" />
    </svg>
  );
}
